class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('.cart-notification__close, .cart-notification__links .link').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    this.notification.addEventListener(
      'change',
      debounce((evt) => {
        const input = evt.target.closest('.quantity__input');
        if (!input) return;
        this.updateQuantity(input.dataset.quantityLineKey, parseInt(input.value, 10));
      }, 300)
    );

    this.notification.addEventListener('click', (evt) => {
      const removeButton = evt.target.closest('.cart-notification-remove-button');
      if (!removeButton) return;
      evt.preventDefault();
      this.updateQuantity(removeButton.dataset.lineKey, 0);
    });
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  // Updates the quantity (or removes, when quantity is 0) of the single
  // line item currently shown in the notification, via the same Cart
  // Ajax API + section-rendering pattern used elsewhere in the theme —
  // scoped to just this popup, since it only ever shows one item at a
  // time and isn't aware of the rest of the cart's contents/positions.
  updateQuantity(lineKey, quantity) {
    if (!lineKey) return;

    const body = JSON.stringify({
      id: lineKey,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        if (parsedState.errors) return;

        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) bubble.innerHTML = this.getSectionInnerHTML(parsedState.sections['cart-icon-bubble']);

        if (quantity === 0) {
          this.close();
          return;
        }

        const product = document.getElementById('cart-notification-product');
        if (product) {
          product.innerHTML = this.getSectionInnerHTML(
            parsedState.sections['cart-notification-product'],
            `[id="cart-notification-product-${lineKey}"]`
          );
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);